import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { cva, type VariantProps } from 'class-variance-authority';

export const logoVariants = cva('inline-flex shrink-0 items-center whitespace-nowrap', {
  variants: {
    size: {
      sm: 'gap-1.25 tracking-[0.02em]',
      lg: 'gap-2 tracking-[0.04em]',
    },
  },
  defaultVariants: { size: 'sm' },
});
export type LogoVariants = VariantProps<typeof logoVariants>;

const markVariants = cva('font-semibold leading-none text-[color:var(--logo-brand)]', {
  variants: { size: { sm: 'text-base', lg: 'text-xl' } },
  defaultVariants: { size: 'sm' },
});

const wordVariants = cva('font-normal not-italic uppercase leading-none tracking-[0.10em] text-c2', {
  variants: {
    size: { sm: 'text-sm', lg: 'text-base' },
    hideWhenNarrow: { true: 'hidden compact:inline', false: '' },
  },
  defaultVariants: { size: 'sm', hideWhenNarrow: false },
});

/**
 * The one two-part wordmark for every surface — shell, editors, wizard, gallery, auth, admin, 404.
 * Defaults to LUMIR PHOTO; a product cabinet carrying its own lockup (STUDIO MANAGER) overrides the
 * two words rather than re-typing the type scale.
 */
@Component({
  selector: 'hlm-logo',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': 'cls()' },
  template: `
    <b [class]="markCls()">{{ mark() }}</b>
    <i [class]="wordCls()">{{ word() }}</i>
  `,
})
export class HlmLogo {
  readonly size = input<NonNullable<LogoVariants['size']>>('sm');
  readonly mark = input('LUMIR');
  readonly word = input('PHOTO');
  readonly hideWordWhenNarrow = input(false);

  protected readonly cls = computed(() => logoVariants({ size: this.size() }));
  protected readonly markCls = computed(() => markVariants({ size: this.size() }));
  protected readonly wordCls = computed(() => wordVariants({ size: this.size(), hideWhenNarrow: this.hideWordWhenNarrow() }));
}
